import { readonly, Ref, ref, useContext } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import type {
  OrderByFilterParams,
  UseUserOrderErrors,
  UseUserOrderInterface,
  UseUserOrderSearchParams,
  TrackingOrderParams,
} from './useUserOrder';
import { getApiAxios } from "~/helpers";
import api from "~/config/extensionsApi";


/**
 * Allows fetching customer orders.
 *
 * See the {@link UseUserOrderInterface} for a list of methods and values available in this composable.
 */
export function useUserOrder(): UseUserOrderInterface {
  const { app } = useContext();
  const loading = ref(false);
  const error = ref<UseUserOrderErrors>({
    search: null,
    orderList: null,
    trackingOrder: null
  });

  const search = async (params: UseUserOrderSearchParams) => {
    let results = null;

    try {
      loading.value = true;

      Logger.debug('[Magento] search user orders', { params });

      const { data } = await app.$vsf.$magento.api.customerOrders(params, params?.customQuery ?? null, params?.customHeaders);

      Logger.debug('[Result]:', { data });

      results = data?.customer?.orders ?? null;
      error.value.search = null;
    } catch (err) {
      error.value.search = err;
      Logger.error('useRelatedProducts/search', err);
    } finally {
      loading.value = false;
    }

    return results;
  };

  const listOrderByFilter = async (params: OrderByFilterParams) => {
    if (!params.phone_number || !params.customerId || !params.limit) {
      Logger.error('useUserOrder/listOrderByFilter: ', 'params phone_number, customerId, limit is required');
      return;
    }

    let results = null;
    try {
      loading.value = true;
      const response = await getApiAxios(api.customer.customer_list_order_by_filter, {}, {
        phone_number: params.phone_number,
        customerId: params.customerId,
        limit: params.limit,
        offset: params.offset,
      });

      const json = response;
      if (json.code !== 200 || json.result.errors) {
        error.value.orderList = json.result.errors;
        Logger.error('useUserOrder/listOrderByFilter: ', json.result);
      } else {
        results = json?.result ?? null;
        error.value.orderList = null;
      }
    } catch (err) {
      error.value.orderList = err;
      Logger.error('useUserOrder/listOrderByFilter', err);

    } finally {
      loading.value = false;
    }

    return results;

  };

  const customerTrackingOrder = async (params: TrackingOrderParams) => {
    if (!params.order_code) {
      Logger.error('useUserOrder/customerTrackingOrder: ', 'params order_code is required');
      return;
    }

    let results = null;
    try {
      loading.value = true;
      const response = await getApiAxios(api.erp.tracking_order, {}, {
        order_code: params.order_code
      });

      const json = response;
      if (json.code !== 200 || json.result.errors) {
        error.value.trackingOrder = json.result.errors;
        Logger.error('useUserOrder/customerTrackingOrder: ', json.result);
      } else {
        results = json?.result ?? null;
        error.value.trackingOrder = null;
      }
    } catch (err) {
      error.value.trackingOrder = err;
      Logger.error('useUserOrder/customerTrackingOrder', err);

    } finally {
      loading.value = false;
    }

    return results;

  };

  return {
    search,
    listOrderByFilter,
    customerTrackingOrder,
    loading: readonly(loading),
    error: readonly(error),
  };
}

export default useUserOrder;
export * from './useUserOrder';
